import { UserRole } from '@/permissions/types';
import { useBalticStore } from '@/store/balticStore';
import { usePositionListStore } from '@/store/positionListStore';
import { useBunkerPricesStore } from '@/store/bunkerPricesStore';
import { useFixturesStore } from '@/store/fixturesStore';
import { useRecentFixturesStore } from '@/store/recentFixturesStore';
import { useCargoListStore } from '@/store/cargoListStore';
import { useCargoCountStore } from '@/store/cargoCountStore';
import { useFixedDaysStore } from '@/store/fixedDaysStore';

/**
 * Configuration for background data refresh
 * Maps routes to their data loading functions
 */
interface RouteRefreshConfig {
  path: string | RegExp;
  roles: UserRole[];
  refreshFn: () => Promise<void>;
  description: string;
}

/**
 * Background Refresh Service
 * Silently refreshes data in the background when users visit pages
 * Uses cached data for instant display while fetching fresh data
 */
export class BackgroundRefreshService {
  private static refreshConfigs: RouteRefreshConfig[] = [
    {
      path: '/baltic',
      roles: [UserRole.BALTIC],
      refreshFn: async () => {
        const store = useBalticStore.getState();
        // fetchRoutes already has caching logic - will use cache if fresh
        // but we want to refresh in background, so clear the timestamp
        await store.refreshData();
        console.log('[BackgroundRefresh] Baltic data refreshed');
      },
      description: 'Baltic routes and history',
    },
    {
      path: /^\/position-list/,
      roles: [UserRole.POSITION_LIST],
      refreshFn: async () => {
        const store = usePositionListStore.getState();
        // Fetch areas if not already loaded
        if (store.areas.length === 0) {
          await store.fetchAreas();
        }
        // Refresh positions for current area in background
        if (store.selectedArea) {
          // Don't show loading spinner - silent background refresh
          await store.refreshPositions();
          console.log('[BackgroundRefresh] Position list data refreshed');
        }
      },
      description: 'Position list data',
    },
    {
      path: '/bunker-prices',
      roles: [UserRole.BUNKER_PRICES],
      refreshFn: async () => {
        const store = useBunkerPricesStore.getState();
        // Force refresh to get latest prices
        await store.refreshBunkerPrices();
        console.log('[BackgroundRefresh] Bunker prices refreshed');
      },
      description: 'Bunker prices',
    },
    {
      path: /^\/fixtures/,
      roles: [UserRole.FIXTURES],
      refreshFn: async () => {
        const store = useFixturesStore.getState();
        // Refresh fixtures data in background
        await store.refreshFixtures();
        console.log('[BackgroundRefresh] Fixtures data refreshed');
      },
      description: 'Fixtures data',
    },
    {
      path: /^\/recent-fixtures/,
      roles: [UserRole.RECENT_FIXTURES],
      refreshFn: async () => {
        const store = useRecentFixturesStore.getState();
        await store.refreshFixtures();
        console.log('[BackgroundRefresh] Recent fixtures data refreshed');
      },
      description: 'Recent fixtures data',
    },
    {
      path: /^\/cargo-list/,
      roles: [UserRole.CARGO_LIST],
      refreshFn: async () => {
        const store = useCargoListStore.getState();
        // Refresh cargo list in background
        await store.refreshCargoList();
        console.log('[BackgroundRefresh] Cargo list data refreshed');
      },
      description: 'Cargo list data',
    },
    {
      path: '/cargo-count',
      roles: [UserRole.CARGO_COUNT],
      refreshFn: async () => {
        const store = useCargoCountStore.getState();
        // Force refresh to ensure latest cargo count values
        await store.refreshData();
        console.log('[BackgroundRefresh] Cargo count data refreshed');
      },
      description: 'Cargo count data',
    },
    {
      path: '/fixed-days',
      roles: [UserRole.FIXED_DAYS],
      refreshFn: async () => {
        const store = useFixedDaysStore.getState();
        // Refresh fixed days data - reset timestamp and fetch
        await store.fetchPools(false, { silent: true });
        console.log('[BackgroundRefresh] Fixed days data refreshed');
      },
      description: 'Fixed days data',
    },
  ];

  /**
   * Check if a path matches a route pattern
   */
  private static pathMatches(pattern: string | RegExp, path: string): boolean {
    if (typeof pattern === 'string') {
      return path === pattern || path.startsWith(pattern + '/');
    }
    return pattern.test(path);
  }

  /**
   * Find refresh configurations that match the current route
   */
  private static findMatchingConfigs(currentPath: string): RouteRefreshConfig[] {
    return this.refreshConfigs.filter((config) => this.pathMatches(config.path, currentPath));
  }

  /**
   * Trigger background refresh for the current route
   * This runs silently without showing loading spinners
   * Uses cached data immediately while fetching fresh data in background
   */
  static async refreshForRoute(currentPath: string, userRoles: UserRole[]): Promise<void> {
    const matchingConfigs = this.findMatchingConfigs(currentPath);

    if (matchingConfigs.length === 0) {
      console.log('[BackgroundRefresh] No refresh needed for:', currentPath);
      return;
    }

    // Filter configs by user permissions
    const authorizedConfigs = matchingConfigs.filter((config) =>
      config.roles.some((role) => userRoles.includes(role))
    );

    if (authorizedConfigs.length === 0) {
      console.log('[BackgroundRefresh] No authorized refresh configs for:', currentPath);
      return;
    }

    console.log(
      `[BackgroundRefresh] Starting background refresh for ${authorizedConfigs.length} data source(s)`
    );

    // Run all refresh functions in parallel without waiting
    // This allows the page to render immediately with cached data
    const refreshPromises = authorizedConfigs.map(async (config) => {
      try {
        await config.refreshFn();
      } catch (error) {
        console.error(`[BackgroundRefresh] Failed to refresh ${config.description}:`, error);
        // Don't throw - allow other refreshes to continue
      }
    });

    // Don't await - let it run in background
    Promise.all(refreshPromises)
      .then(() => {
        console.log('[BackgroundRefresh] All background refreshes completed');
      })
      .catch((error) => {
        console.error('[BackgroundRefresh] Background refresh error:', error);
      });
  }

  /**
   * Check if a route has background refresh configured
   */
  static hasRefreshConfig(currentPath: string): boolean {
    return this.findMatchingConfigs(currentPath).length > 0;
  }

  /**
   * Get description of what will be refreshed for a route
   */
  static getRefreshDescription(currentPath: string, userRoles: UserRole[]): string[] {
    const matchingConfigs = this.findMatchingConfigs(currentPath);
    const authorizedConfigs = matchingConfigs.filter((config) =>
      config.roles.some((role) => userRoles.includes(role))
    );
    return authorizedConfigs.map((config) => config.description);
  }
}
